<div class="quiz-result container-xxl py-6 py-lg-10">

  @if (isLoading()) {
    <div data-testid="quiz-result-loading">
      <div class="skeleton h-150px w-100 rounded-4 mb-6"></div>
      <ng-container *ngTemplateOutlet="resultCardSkeleton"></ng-container>
      <ng-container *ngTemplateOutlet="resultCardSkeleton"></ng-container>
    </div>
  } @else if (error()) {
    <div class="card"><div class="card-body is-empty"><p class="fs-5 text-danger mb-0">{{ error() }}</p></div></div>
  } @else if (attempt(); as a) {

    <!-- The payoff, on ink. The mark was a coloured number on the same white
         card as everything else, so the thing the student came back for looked
         like one more row of the report. -->
    <section class="result-hero overflow-hidden" [class.is-pass]="a.passed">
      <p class="result-hero__quiz fs-7">{{ a.quiz_title }}</p>
      <div class="result-hero__body d-flex flex-wrap align-items-baseline">
        <span class="result-hero__percent fw-bolder" data-testid="score-percent">{{ a.percent }}%</span>
        <span class="result-hero__side d-flex flex-column">
          <span class="result-hero__chip align-self-start fw-medium">
            {{ a.passed ? ("QUIZ.PASSED" | translate) : ("QUIZ.NOT_PASSED" | translate) }}
          </span>
          <!-- Marks, not items. The percent is mark-weighted — a hard question
               is worth more than an easy one — so "75%" over "ตอบถูก 4 / 5"
               read as arithmetic that does not work. The marks are the
               denominator the percent is actually computed from; the item
               count still appears in the answer key below, where each question
               carries its own weight. -->
          <span class="result-hero__count fs-7">
            {{ "QUIZ.SCORE" | translate }} {{ a.score }} / {{ a.max_score }}
            <span class="result-hero__items d-block opacity-75">
              ({{ "QUIZ.CORRECT" | translate }} {{ correctCount() }}/{{ responses().length }} {{ "QUIZ.QUESTION" | translate }})
            </span>
          </span>
        </span>
      </div>
    </section>

    <!-- Difficulty before topic: the first question a student has about a score
         is whether the paper was hard, and until that is answered the topic
         split cannot be read properly either. -->
    @if (byDifficulty().length) {
      <section class="result-card">
        <h2 class="result-card__head fs-5 fw-bold">{{ "QUIZ.BY_DIFFICULTY" | translate }}</h2>
        <div class="meters d-flex flex-column" data-testid="difficulty-breakdown">
          @for (row of byDifficulty(); track row.difficulty) {
            <div class="meter d-grid align-items-center">
              <span class="meter__label">{{ row.difficulty_label }}</span>
              <span class="meter__track d-block h-8px overflow-hidden">
                <span class="meter__fill d-block h-100" [ngClass]="band(row.percent)" [style.width.%]="row.percent"></span>
              </span>
              <span class="meter__count">{{ row.correct }}/{{ row.answered }}</span>
              <span class="meter__value fw-bold">{{ row.percent }}%</span>
            </div>
          }
        </div>
      </section>
    }

    @if (breakdown().length) {
      <section class="result-card">
        <h2 class="result-card__head fs-5 fw-bold">{{ "QUIZ.BY_TOPIC" | translate }}</h2>
        <div class="meters d-flex flex-column" data-testid="skill-breakdown">
          @for (row of breakdown(); track row.sub_lesson_id) {
            <div class="meter d-grid align-items-center">
              <span class="meter__label">{{ row.sub_lesson_name }}</span>
              <span class="meter__track d-block h-8px overflow-hidden">
                <span class="meter__fill d-block h-100" [ngClass]="band(row.percent)" [style.width.%]="row.percent"></span>
              </span>
              <span class="meter__count">{{ row.correct }}/{{ row.total }}</span>
              <span class="meter__value fw-bold">{{ row.percent }}%</span>
            </div>
          }
        </div>
      </section>
    }

    <section class="result-card">
      <h2 class="result-card__head fs-5 fw-bold" data-testid="answer-key-heading">{{ "QUIZ.ANSWER_KEY" | translate }}</h2>

      @for (row of responses(); track row.id; let i = $index) {
        <article class="answer" [class.is-wrong]="!row.is_correct">
          <div class="answer__head d-flex align-items-start">
            <span class="answer__no d-flex align-items-center justify-content-center fw-bold">{{ i + 1 }}</span>
            <span class="answer__q flex-fill fw-medium">{{ row.quiz_question?.body }}</span>
          </div>

          <div class="answer__body">
            <p class="answer__line d-flex flex-wrap">
              <span class="answer__key">{{ "QUIZ.YOUR_ANSWER" | translate }}</span>
              <span class="answer__given" [class.is-right]="row.is_correct">
                {{ chosenText(row) || ("QUIZ.NO_ANSWER" | translate) }}
              </span>
            </p>
            @if (!row.is_correct) {
              <p class="answer__line d-flex flex-wrap">
                <span class="answer__key">{{ "QUIZ.CORRECT_ANSWER" | translate }}</span>
                <span class="answer__given is-right">{{ correctText(row) }}</span>
              </p>
            }
            <!-- Why it was wrong, not just that it was.
                 A student told only "the answer was ข" repeats the mistake next
                 week; naming the trap they fell into is the difference between a
                 mark and a correction. Above the explanation because it is about
                 THEIR answer, not about the question. -->
            @if (!row.is_correct && row.misconception) {
              <div class="answer__note fs-7 lh-lg is-trap" data-testid="misconception">
                <span class="answer__note-head d-block fw-bold">{{ "QUIZ.MISCONCEPTION" | translate }}</span>
                {{ row.misconception }}
              </div>
            }
            @if (row.quiz_question?.explanation) {
              <div class="answer__note fs-7 lh-lg is-why">
                <span class="answer__note-head d-block fw-bold">{{ "QUIZ.EXPLANATION" | translate }}</span>
                {{ row.quiz_question?.explanation }}
              </div>
            }
          </div>
        </article>
      }
    </section>

    <div class="result-actions d-flex">
      <a class="btn btn-light flex-grow-1" routerLink="/online-class/quiz">{{ "QUIZ.BACK_TO_LIST" | translate }}</a>
      <a class="btn btn-primary flex-grow-1" routerLink="/profile/monitor/skills">{{ "QUIZ.MY_SKILLS" | translate }}</a>
    </div>
  }
</div>

<ng-template #resultCardSkeleton>
  <section class="result-card">
    <div class="skeleton h-25px w-150px mb-6"></div>
    <div class="d-flex align-items-center gap-3 mb-4">
      <div class="skeleton h-15px w-125px"></div>
      <div class="skeleton h-8px flex-grow-1"></div>
      <div class="skeleton h-15px w-40px"></div>
    </div>
    <div class="d-flex align-items-center gap-3 mb-4">
      <div class="skeleton h-15px w-125px"></div>
      <div class="skeleton h-8px flex-grow-1"></div>
      <div class="skeleton h-15px w-40px"></div>
    </div>
    <div class="d-flex align-items-center gap-3">
      <div class="skeleton h-15px w-125px"></div>
      <div class="skeleton h-8px flex-grow-1"></div>
      <div class="skeleton h-15px w-40px"></div>
    </div>
  </section>
</ng-template>
